import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import 'src/common/styles/global.css';
import { Input, Button, Badge, useAppSettings } from 'src/common';
import { Environment, VqaThumbnailAppearance } from 'src/common/types';
import { ArrowLeft, Send, MessageSquareText, ChevronDown, X, ThumbsUp, ThumbsDown, Play } from 'lucide-react';
import { NotificationBanner } from '../../../components/NotificationBanner';
import { formatNotificationMessage } from 'src/common/utils/notificationUtils';


import { VideoPlayer } from './VideoPlayer';
import { ChatMessageType, ChatMessage, Event, NotificationSource } from '../types/chat';
import { VQAResponse, WebSocketAction } from 'src/common/types'
import { MarkdownRenderer } from './MarkdownRenderer';
import { websocketManager, serviceManager, AppApiGatewayService } from 'src/common/services';
import { shouldSendNotificationMessageToVQA, checkNotificationSource } from '../utils/notificationUtils';
import { NotificationVideoHandler, VQAVideoHandler, fetchVideoForEvent } from '../utils/videoHandlers';


interface RogersVQAProps {
    onBack: () => void;
    onClose: () => void;
}

export function RogersVQA({ onBack, onClose }: RogersVQAProps) {
    const { developerMode, environment, vqaThumbnailAppearance, vlmModel, llmModel } = useAppSettings();
    const location = useLocation();
    const [backendService, setBackendService] = useState<AppApiGatewayService | null>(null);
    const [question, setQuestion] = useState('');
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [visibleResults, setVisibleResults] = useState<{ [key: number]: number }>({});
    const [isVideoPlayerOpen, setIsVideoPlayerOpen] = useState(false);
    const [initialVideoId, setInitialVideoId] = useState<string>('');
    const [allVideos, setAllVideos] = useState<Event[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const streamingMessageIdRef = useRef<number | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const requestTimestamps = useRef<Map<string, number>>(new Map());
    // Tracks if navigation notification message has been sent
    const navigationMessageSentRef = useRef<boolean>(false);
    // Dictionary to track messageId -> {eventId, source} mapping for notifications
    const notificationMessageMapRef = useRef<Map<string, { eventId: string, source: NotificationSource }>>(new Map());

    // Video handlers
    const [notificationVideoHandler, setNotificationVideoHandler] = useState<NotificationVideoHandler | null>(null);
    const [vqaVideoHandler, setVQAVideoHandler] = useState<VQAVideoHandler | null>(null);

    // Track blob URLs for cleanup to prevent memory leaks
    const blobUrlsRef = useRef<Set<string>>(new Set());

    const calculateResponseTime = (messageId: string, cleanup: boolean = false) => {
        const storedTimestamp = requestTimestamps.current.get(messageId);
        const timeTaken = storedTimestamp ? Date.now() - storedTimestamp : undefined;
        if (cleanup) requestTimestamps.current.delete(messageId);
        return timeTaken;
    };

    useEffect(() => {
        // Get service on mount
        const initializeService = async () => {
            const manager = await serviceManager;
            const service = manager.getService();
            setBackendService(service);
            if (service) {
                // Initialize video handlers
                const notificationHandler = new NotificationVideoHandler(service, setConversations);
                const vqaHandler = new VQAVideoHandler(service, setConversations);

                setNotificationVideoHandler(notificationHandler);
                setVQAVideoHandler(vqaHandler);
            }
        };

        initializeService();
    }, []);

    useEffect(() => {
        websocketManager.addListener(handleVQAResponse);
        return () => websocketManager.removeListener(handleVQAResponse);
    }, [backendService]);

    // Revoke blob URLs on unmount to prevent memory leaks
    useEffect(() => {
        const urls = blobUrlsRef.current;
        return () => {
            urls.forEach((url) => {
                URL.revokeObjectURL(url);
            });
            urls.clear();
        };
    }, []);

    const [conversations, setConversations] = useState<ChatMessage[]>([]);
    const conversationsRef = useRef(conversations);

    useEffect(() => {
        conversationsRef.current = conversations;
    }, [conversations]);

    // Track blob URLs from conversation events so we can revoke them on cleanup
    useEffect(() => {
        conversations.forEach((msg) => {
            msg.events?.forEach((event) => {
                if (event.isBlobUrl && event.videoUrl) {
                    blobUrlsRef.current.add(event.videoUrl);
                }
            });
        });
    }, [conversations]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [conversations, visibleResults]);

    // Handle initial notification message from navigation (when user clicks banner from main page)
    useEffect(() => {
        // Extract navigation state passed from previous page (e.g., notification banner click)
        const state = location.state as { initialMessage?: string; eventId?: string };
        // Only process initial message if:
        // 1. Navigation state contains an initial message (from notification banner click)
        // 2. Chat is empty (prevents duplicate messages on re-renders)
        // 3. Haven't already sent the initial message (prevents React StrictMode duplicates)
        if (shouldSendNotificationMessageToVQA(location.state, conversations.length, navigationMessageSentRef.current)) {
            navigationMessageSentRef.current = true; // Mark as sent to prevent duplicates

            // Send the notification template message to VQA Agent instead of showing it directly
            const messageId = crypto.randomUUID();
            // Store messageId -> {eventId, source} mapping
            if (state.eventId) {
                notificationMessageMapRef.current.set(messageId, {
                    eventId: state.eventId,
                    source: NotificationSource.NAVIGATION
                });
            } else {
                console.error('🚨 Navigation notification missing eventId:', state);
            }
            requestTimestamps.current.set(messageId, Date.now());

            // Send notification template message to VQA Agent via WebSocket
            websocketManager.send({
                messageId,
                action: WebSocketAction.VQA_QUERY,
                query: state.initialMessage!,
                mode: environment,
                llmModel: llmModel,
                vlmModel: vlmModel,
            });

            setIsProcessing(true);
        }
    }, [location.state, conversations.length]); // Remove history and environment from dependencies

    const handleVQAResponse = async (event: MessageEvent) => {
        try {
            const response = JSON.parse(event.data) as VQAResponse;
            console.log('📬 VQA Response received:', response);

            // Filter for VQA_query action only
            if (response.action !== WebSocketAction.VQA_QUERY) {
                return;
            }

            // Clear states for successful responses
            if (streamingMessageIdRef.current === response.prompt_creation_time) {
                setIsProcessing(false);
            }

            // Check for error in response first
            if (response.error) {
                setConversations(prev => {
                    const targetMessageIndex = prev.findIndex(m => m.timestamp === response.prompt_creation_time);
                    if (targetMessageIndex !== -1 && prev[targetMessageIndex].type === ChatMessageType.BOT) {
                        const updated = [...prev];
                        const errorMessage: ChatMessage = {
                            ...updated[targetMessageIndex],
                            content: "",
                            reasoning: "",
                            events: undefined,
                            isStreaming: false,
                            error: "Sorry, I encountered an error while processing your request. Please try again."
                        };

                        updated[targetMessageIndex] = errorMessage;
                        return updated;
                    }
                    return prev;
                });
                return;
            }

            // Handle full response
            if (response.type == 'full') {
                if (streamingMessageIdRef.current === response.prompt_creation_time) {
                    streamingMessageIdRef.current = null;
                }

                const timeTaken = calculateResponseTime(response.messageId, true);

                // Update or create message
                setConversations(prev => {
                    const messageIndex = prev.findIndex(m => m.timestamp === response.prompt_creation_time);
                    const updated = [...prev];
                    if (messageIndex !== -1 && prev[messageIndex].type === ChatMessageType.BOT) {
                        // Update existing message
                        updated[messageIndex] = {
                            ...updated[messageIndex],
                            content: response.text || "",
                            events: undefined,
                            isStreaming: false,
                            reasoning: '',
                            error: undefined,
                            totalTime: timeTaken
                        };
                    } else if (messageIndex === -1) {
                        // Create new message and insert in correct chronological position
                        const newMessage: ChatMessage = {
                            type: ChatMessageType.BOT,
                            content: response.text || '',
                            reasoning: '',
                            timestamp: response.prompt_creation_time,
                            isStreaming: false,
                            totalTime: timeTaken,
                            llmModel: response.llmModel,
                            vlmModel: response.vlmModel,
                        };

                        // Find correct insertion point based on timestamp
                        const insertIndex = updated.findIndex(m => m.timestamp > response.prompt_creation_time);
                        if (insertIndex === -1) {
                            updated.push(newMessage);
                        } else {
                            updated.splice(insertIndex, 0, newMessage);
                        }
                    } else {
                        console.warn('🔵 Found message but not BOT type:', prev[messageIndex]);
                    }

                    return updated;
                });

                // Log if response is missing text content
                if (!response.text) {
                    console.error('VQA response missing text content:', response);
                }

                // Handle notification video or regular VQA events (mutually exclusive)
                const notificationData = notificationMessageMapRef.current.get(response.messageId);
                console.log('🔍 MessageId lookup - response:', response.messageId, 'found:', notificationData);
                if (notificationData) {
                    if (notificationVideoHandler) {
                        console.log('🎥 Calling notificationVideoHandler.handle -', notificationData.source);
                        await notificationVideoHandler.handle(response, location.state, notificationData.source, notificationMessageMapRef, () => {
                            notificationMessageMapRef.current.delete(response.messageId); // Remove from map
                        });
                    }
                } else if (vqaVideoHandler) {
                    console.log('🎥 Calling vqaVideoHandler.handle');
                    await vqaVideoHandler.handle(response);
                }
            } else if (response.type === 'data' || (response.text !== undefined || response.reasoning !== undefined)) {
                // Handle streaming responses (text/reasoning)
                setConversations(prev => {
                    const targetMessageIndex = prev.findIndex(m => m.timestamp === response.prompt_creation_time);
                    const targetMessage = targetMessageIndex !== -1 ? prev[targetMessageIndex] : null;
                    const { text, reasoning } = response;

                    // Skip if target message already has an error
                    if (targetMessage?.error) {
                        return prev;
                    }

                    if (targetMessage && targetMessage.type === ChatMessageType.BOT && targetMessage.isStreaming) {
                        const updated = [...prev];
                        const updatedMessage: ChatMessage = {
                            ...targetMessage,
                            reasoning: reasoning,
                            content: targetMessage.content + (text || ""),
                            isStreaming: true,
                            error: undefined
                        };

                        updated[targetMessageIndex] = updatedMessage;
                        return updated;
                    } else if (targetMessageIndex === -1) {
                        // First token received - calculate TTFT
                        const timeTaken = calculateResponseTime(response.messageId);

                        // Create new BOT message when no message exists
                        const newMessage: ChatMessage = {
                            type: ChatMessageType.BOT,
                            content: text || '',
                            reasoning: reasoning || '',
                            timestamp: response.prompt_creation_time,
                            isStreaming: true,
                            responseTime: timeTaken,
                            llmModel: response.llmModel,
                            vlmModel: response.vlmModel,
                        };
                        setIsProcessing(false);
                        streamingMessageIdRef.current = response.prompt_creation_time;
                        return [...prev, newMessage];
                    }

                    return prev;
                });
            }
        } catch (error) {
            console.error('Error parsing WebSocket message:', error);
            setIsProcessing(false);
            setConversations(prev => {
                // Clear any streaming message on error
                if (streamingMessageIdRef.current !== null) {
                    const updated = [...prev];
                    const streamingIndex = updated.findIndex(m => m.timestamp === streamingMessageIdRef.current);
                    if (streamingIndex !== -1) {
                        const errorMessage: ChatMessage = {
                            ...updated[streamingIndex],
                            content: "",
                            reasoning: "",
                            events: undefined,
                            isStreaming: false,
                            error: "Sorry, I encountered an error while processing your request. Please try again."
                        };
                        updated[streamingIndex] = errorMessage;
                    }
                    streamingMessageIdRef.current = null;
                    return updated;
                }
                return prev;
            });
        }
    }

    const loadMoreResults = (messageId: number) => {
        const currentVisible = visibleResults[messageId] || 6;
        const messageIndex = conversations.findIndex(msg => msg.timestamp === messageId);
        if (messageIndex === -1) return;

        const message = conversations[messageIndex];
        const totalEvents = message?.events?.length || 0;
        const newVisible = Math.min(currentVisible + 6, totalEvents);

        if (newVisible <= currentVisible) return;

        // Get events that need to be fetched, tracking their original indices
        if (backendService && message?.events) {
            const eventsSlice = message.events.slice(currentVisible, newVisible);
            const eventsWithOriginalIndices = eventsSlice
                .map((event, sliceIndex) => ({ event, originalIndex: currentVisible + sliceIndex }))
                .filter(({ event }) => event.needsFetch);

            if (!eventsWithOriginalIndices.length) {
                setVisibleResults(prev => ({
                    ...prev,
                    [messageId]: newVisible
                }));
                return;
            }

            // Set loading state for the new events first
            setConversations(current => {
                const currentMsgIndex = current.findIndex(m => m.timestamp === messageId);
                if (currentMsgIndex === -1) return current;

                const updatedMessage = { ...current[currentMsgIndex] };
                const updatedEventsList = [...updatedMessage.events!];

                eventsWithOriginalIndices.forEach(({ event, originalIndex }) => {
                    if (originalIndex < updatedEventsList.length) {
                        updatedEventsList[originalIndex] = {
                            ...event,
                            isLoading: true,
                            needsFetch: false
                        };
                    }
                });

                updatedMessage.events = updatedEventsList;
                const updated = [...current];
                updated[currentMsgIndex] = updatedMessage;
                return updated;
            });

            // Update visible count immediately to show loading placeholders
            setVisibleResults(prev => ({
                ...prev,
                [messageId]: newVisible
            }));

            // Fetch videos individually and update as they complete
            eventsWithOriginalIndices.forEach(async ({ event, originalIndex }) => {
                try {
                    const updatedEvent = await fetchVideoForEvent(backendService!, event.id);

                    setConversations(current => {
                        const currentMsgIndex = current.findIndex(m => m.timestamp === messageId);
                        if (currentMsgIndex === -1) return current;

                        const updatedMessage = { ...current[currentMsgIndex] };
                        if (!updatedMessage.events) return current;
                        const updatedEventsList = [...updatedMessage.events];

                        if (originalIndex < updatedEventsList.length) {
                            updatedEventsList[originalIndex] = updatedEvent;
                        }

                        updatedMessage.events = updatedEventsList;
                        const updated = [...current];
                        updated[currentMsgIndex] = updatedMessage;
                        return updated;
                    });
                } catch (error) {
                    console.error(`Failed to fetch video for event ${event.id}:`, error);

                    setConversations(current => {
                        const currentMsgIndex = current.findIndex(m => m.timestamp === messageId);
                        if (currentMsgIndex === -1) return current;

                        const updatedMessage = { ...current[currentMsgIndex] };
                        if (!updatedMessage.events) return current;
                        const updatedEventsList = [...updatedMessage.events];

                        if (originalIndex < updatedEventsList.length) {
                            updatedEventsList[originalIndex] = {
                                ...event,
                                hasError: true,
                                isLoading: false
                            };
                        }

                        updatedMessage.events = updatedEventsList;
                        const updated = [...current];
                        updated[currentMsgIndex] = updatedMessage;
                        return updated;
                    });
                }
            });
        }
    };

    const getVisibleCount = (messageId: number) => {
        return visibleResults[messageId] || 6;
    };

    const handleVideoClick = (video: Event, videos: Event[]) => {
        setInitialVideoId(video.id);
        setAllVideos(videos);
        setIsVideoPlayerOpen(true);
    };

    /**
     * Handle notification banner clicks from within the VQA page
     * Tracks the notification data and sends template message to VQA
     * Different from navigation-based notifications which use location.state
     */
    const handleInPageNotificationClick = (notificationData: any) => {
        // Track this notification click for later video thumbnail logic
        const messageId = crypto.randomUUID();

        // Store messageId -> {eventId, source} mapping for notification flow tracking
        notificationMessageMapRef.current.set(messageId, {
            eventId: notificationData.eventId,
            source: NotificationSource.IN_PAGE
        });

        // Format notification data into a structured chat message using shared utility
        const chatMessage = formatNotificationMessage(notificationData);

        // Send to VQA Agent
        requestTimestamps.current.set(messageId, Date.now());

        websocketManager.send({
            messageId,
            action: WebSocketAction.VQA_QUERY,
            query: chatMessage,
            mode: environment,
            llmModel: llmModel,
            vlmModel: vlmModel,
        });

        setIsProcessing(true);
    };

    const handleSendQuestion = () => {
        if (!question.trim()) return;

        const timestamp = Date.now();
        const messageId = crypto.randomUUID();
        requestTimestamps.current.set(messageId, timestamp);

        // Send search query via WebSocket
        websocketManager.send({
            messageId,
            action: WebSocketAction.VQA_QUERY,
            query: question.trim(),
            mode: environment,
            llmModel: llmModel,
            vlmModel: vlmModel,
        });

        const userMessage: ChatMessage = {
            type: ChatMessageType.USER,
            content: question,
            timestamp
        };

        // Stop any previous streaming message
        if (streamingMessageIdRef.current) {
            setConversations(prev => prev.map(msg =>
                msg.timestamp === streamingMessageIdRef.current ? { ...msg, isStreaming: false } : msg
            ));
            streamingMessageIdRef.current = null;
        }

        setConversations(prev => [...prev, userMessage]);
        setIsProcessing(true);
        setQuestion('');
    };

    return (
        <div className="min-h-screen bg-background z-50 flex flex-col">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-background flex-shrink-0 flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={onBack}>
                        <ArrowLeft className="w-5 h-5 text-muted-foreground" />
                    </Button>
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                            <MessageSquareText className="w-4 h-4 text-white" />
                        </div>
                        <div className="h-10">
                            <h1 className="text-foreground !text-base font-medium !leading-none">Rogers VQA</h1>
                            <p className="text-xs text-muted-foreground !leading-none">Visual Question Answering</p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Badge className={environment === Environment.PROTOTYPE
                        ? "bg-orange-500/20 text-orange-400 light:text-orange-600 border-orange-500/30 text-xs"
                        : "bg-blue-500/20 text-blue-400 light:text-blue-600 border-blue-500/30 text-xs"
                    }>
                        {environment === Environment.PROTOTYPE ? 'Prototype' : 'Prod'}
                    </Badge>
                    {developerMode && (
                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                            Dev Tool
                        </Badge>
                    )}
                </div>
            </div>

            {/* Conversation Area */}
            <div className="flex-1 min-h-0 p-4 overflow-y-auto max-w-4xl mx-auto w-full relative">
                <NotificationBanner createNewSession={false} onNotificationClick={handleInPageNotificationClick} />
                {conversations.map((msg) => (
                    <div key={msg.timestamp}>
                        {msg.type === 'user' && (
                            <div className="flex justify-end mb-4">
                                <div className="max-w-[80%] px-4 pt-4 pb-1 rounded-2xl bg-blue-500 text-white">
                                    <p className="text-sm">{msg.content}</p>
                                </div>
                            </div>
                        )}

                        {msg.type === ChatMessageType.BOT && (
                            <div className="mb-4">
                                {/* Bot Avatar and Name */}
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                                        <span className="text-white text-sm font-medium">S</span>
                                    </div>
                                    <div>
                                        <div className="text-sm text-muted-foreground font-medium">Sam</div>
                                        <div className="text-xs text-muted-foreground">Security Guard</div>
                                    </div>
                                </div>

                                <div>
                                    {msg.error && (
                                        <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
                                            <div className="flex items-center gap-2 text-red-400">
                                                <X className="w-4 h-4" />
                                                <span className="text-sm font-medium">Error</span>
                                            </div>
                                            <div className="text-sm text-red-300 mt-1">{msg.error}</div>
                                        </div>
                                    )}

                                    {!msg.error && (
                                        <div className="bg-card border border-border p-4 rounded-lg">
                                            {developerMode && (msg.responseTime !== undefined || msg.totalTime !== undefined || environment === Environment.PROTOTYPE) && (
                                                <div className="text-xs text-muted-foreground mb-2 pb-2 border-b border-border space-y-1">
                                                    {environment === Environment.PROTOTYPE && (
                                                        <div className="flex items-center gap-1">
                                                            {msg.llmModel && (
                                                                <span>
                                                                    <span className="text-blue-500">●</span>{' '}
                                                                    <span className="text-blue-500 font-semibold">LLM: {msg.llmModel}</span>
                                                                </span>
                                                            )}
                                                            {msg.llmModel && msg.vlmModel && (
                                                                <span className="mx-1">·</span>
                                                            )}
                                                            {msg.vlmModel && (
                                                                <span>
                                                                    <span className="text-purple-500">●</span>{' '}
                                                                    <span className="text-purple-500 font-semibold">VLM: {msg.vlmModel}</span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {msg.responseTime !== undefined && (
                                                        <div>First token: {(msg.responseTime / 1000).toFixed(2)}s</div>
                                                    )}
                                                    {msg.totalTime !== undefined && (
                                                        <div>Total: {(msg.totalTime / 1000).toFixed(2)}s</div>
                                                    )}
                                                </div>
                                            )}

                                            {msg.reasoning && !msg.content.trim() && (
                                                <div className="flex text-foreground items-center gap-3">
                                                    <span className="font-medium">{msg.reasoning} </span>
                                                </div>
                                            )}

                                            {msg.content.trim() && (
                                                <div className="text-sm text-foreground">
                                                    <MarkdownRenderer content={msg.content} />
                                                </div>
                                            )}

                                            {/* Video Results Grid */}
                                            {msg.events && (
                                                <div className="space-y-3 mt-4">
                                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-2xl">
                                                        {msg.events.slice(0, getVisibleCount(msg.timestamp)).map((video: Event) => (
                                                            <div
                                                                key={video.id}
                                                                className="relative group cursor-pointer"
                                                                onClick={() => !video.isLoading && !video.hasError && handleVideoClick(video, msg.events || [])}
                                                            >
                                                                <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-card">
                                                                    {/* Loading State */}
                                                                    {video.isLoading && (
                                                                        <div className="w-full h-full flex items-center justify-center">
                                                                            <div className="flex flex-col items-center gap-2">
                                                                                <div className="flex space-x-1">
                                                                                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                                                                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                                                                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                                                                                </div>
                                                                                <span className="text-xs text-gray-300">Loading...</span>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* Error State */}
                                                                    {video.hasError && (
                                                                        <div className="w-full h-full flex items-center justify-center bg-card">
                                                                            <div className="flex flex-col items-center gap-2">
                                                                                <X className="w-6 h-6 text-muted-foreground" />
                                                                                <span className="text-xs text-muted-foreground">Failed to load</span>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* Video Content - only show when loaded successfully */}
                                                                    {!video.isLoading && !video.hasError && video.videoUrl && (
                                                                        <>
                                                                            <video
                                                                                className="w-full h-full object-cover"
                                                                                muted
                                                                                preload="metadata"
                                                                                onLoadedMetadata={(e) => {
                                                                                    const videoEl = e.target as HTMLVideoElement;
                                                                                    videoEl.currentTime = vqaThumbnailAppearance === VqaThumbnailAppearance.MIDDLE_FRAME
                                                                                        ? videoEl.duration / 2
                                                                                        : 0.01;
                                                                                }}
                                                                            >
                                                                                <source src={video.videoUrl} type="video/mp4" />
                                                                                {`Video ${video.id}`}
                                                                            </video>
                                                                            {/* Play icon overlay */}
                                                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                                                                                <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                                                                                    <Play className="w-6 h-6 text-gray-900 ml-1" />
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    )}

                                                                    {/* Timestamp - only show when video loaded successfully */}
                                                                    {!video.isLoading && !video.hasError && video.videoUrl && (
                                                                        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                                                                            <p className="text-xs text-foreground">{video.timestamp?.toLocaleTimeString()}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Thumbs Up/Down and See More */}
                                                    <div className="flex items-center justify-between" >
                                                        <div className="flex items-center gap-3">
                                                            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-blue-400 p-2">
                                                                <ThumbsUp className="w-5 h-5" />
                                                            </Button>
                                                            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-400 p-2">
                                                                <ThumbsDown className="w-5 h-5" />
                                                            </Button>
                                                        </div>

                                                        {
                                                            msg.events && getVisibleCount(msg.timestamp) < msg.events.length && (
                                                                <Button
                                                                    onClick={() => loadMoreResults(msg.timestamp)}
                                                                    className="text-blue-400 hover:text-blue-300 hover:bg-transparent p-2 h-auto"
                                                                    variant="ghost"
                                                                >
                                                                    See More
                                                                    <ChevronDown className="w-5 h-5 ml-1" />
                                                                </Button>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                {/* Processing Animation */}
                {isProcessing && (
                    <div className="mb-4">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-sm font-medium">S</span>
                            </div>
                            <div>
                                <div className="text-sm text-muted-foreground font-medium">Sam</div>
                                <div className="text-xs text-muted-foreground">Security Guard</div>
                            </div>
                        </div>
                        <div className="bg-card border border-border p-4 rounded-lg">
                            <div className="flex items-center gap-3">
                                <div className="flex space-x-1">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                                </div>
                                <span className="text-sm text-foreground animate-pulse">Analyzing your request...</span>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="sticky bottom-0 bg-background flex-shrink-0 p-4 border-t border-border max-w-4xl mx-auto w-full">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <Input
                            value={question}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuestion(e.target.value)}
                            placeholder="Ask anything ..."
                            className="bg-card border-border text-foreground"
                            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && handleSendQuestion()}
                        />
                    </div>
                    <Button
                        onClick={handleSendQuestion}
                        disabled={!question.trim()}
                        className="bg-blue-500 hover:bg-blue-600 text-foreground"
                    >
                        <Send className="w-4 h-4" />
                    </Button>
                </div>
            </div>
            {/* Video Player Dialog */}
            <VideoPlayer
                isOpen={isVideoPlayerOpen}
                initialVideoId={initialVideoId}
                allVideos={allVideos}
                onClose={() => setIsVideoPlayerOpen(false)}
                soundEnabled={soundEnabled}
            />
        </div >
    );
}